import { ExternalLink, Github } from 'lucide-react'
import React from 'react'
import type { Project } from '../../data/projects'
import { categories } from '../../data/projects'
import { Card, Heading, Link, Text } from '../ui'

/**
 * ProjectCard component for displaying individual project information
 *
 * Features:
 * - Responsive card layout with featured project highlighting
 * - Category badge with semantic labeling
 * - Achievement list with check marks
 * - Technology stack display with tags
 * - External links to live demos and source code
 * - Accessibility support with proper ARIA labels
 */

export interface ProjectCardProps {
  project: Project
  className?: string
}

export const ProjectCard = React.forwardRef<HTMLDivElement, ProjectCardProps>(
  ({ project, className = '' }, ref) => {
    const categoryInfo = categories.find(c => c.id === project.category)

    return (
      <Card
        ref={ref}
        variant="surface"
        padding="lg"
        className={`project-card ${project.featured ? 'featured' : ''} ${className}`}
        role="article"
        aria-labelledby={`project-title-${project.id}`}
      >
        <div
          className="category-badge"
          aria-label={`Category: ${categoryInfo?.label}`}
        >
          {categoryInfo?.label || project.category}
        </div>

        <div className="project-header">
          <div className="project-title">
            <Heading
              as="h3"
              size="lg"
              variant="default"
              id={`project-title-${project.id}`}
            >
              {project.title}
            </Heading>
          </div>
          <div
            className="project-links"
            role="group"
            aria-label="Project links"
          >
            {project.link && (
              <Link
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
                aria-label={`View live demo of ${project.title}`}
              >
                <ExternalLink size={16} aria-hidden="true" />
              </Link>
            )}
            {project.github && (
              <Link
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
                aria-label={`View source code for ${project.title}`}
              >
                <Github size={16} aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>

        <Text size="base" variant="secondary" className="project-description">
          {project.description}
        </Text>

        <div className="project-achievements">
          <Text
            size="sm"
            variant="tertiary"
            style={{ fontWeight: 600, marginBottom: '0.5rem' }}
          >
            Key Achievements:
          </Text>
          <ul
            className="achievements-list"
            role="list"
            aria-label={`Achievements for ${project.title}`}
          >
            {project.achievements.slice(0, 4).map((achievement, index) => (
              <li key={index} role="listitem">
                {achievement}
              </li>
            ))}
          </ul>
        </div>

        <div className="project-footer">
          <div
            className="tech-stack"
            role="group"
            aria-label={`Technologies used in ${project.title}`}
          >
            {project.technologies.map(tech => (
              <span key={tech} className="tech-tag" role="text">
                {tech}
              </span>
            ))}
          </div>
          {project.metrics && (
            <div
              className="project-metrics"
              aria-label={`Project metrics: ${project.metrics}`}
            >
              {project.metrics}
            </div>
          )}
        </div>
      </Card>
    )
  }
)

ProjectCard.displayName = 'ProjectCard'

export default ProjectCard
